import { useRef, useEffect, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

interface HeroSolidBGProps {
  tokenPrefix?: string;
  title?: string;
  blurbField?: string;
  defaultText?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  /** Set false to hide the CTA button (e.g. on the Amenities hero). */
  showCta?: boolean;
}

// Background image slot group, mirroring the homepage Hero's `featuredImageDef`
// so this page hero renders the same parallax + Ken Burns photo treatment.
const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

export function HeroSolidBG({
  tokenPrefix,
  title = 'Page',
  blurbField,
  defaultText,
  ctaLabel = 'View Floor Plans',
  ctaUrl = '/floor-plans',
  showCta = true,
}: HeroSolidBGProps) {
  const prefix = tokenPrefix || 'herosolidbg';
  const titlePath = `${prefix}.title`;
  const blurbPath = `${prefix}.blurb`;
  const ctaPath = `${prefix}.cta_link`;
  const bgPath = `${prefix}.bg_image`;

  // The default URL tracks the default CTA label so the link target matches
  // the verb ("View Floor Plans" → /floor-plans). Each page in pages.json
  // is expected to pass `ctaLabel` and `ctaUrl` together when overriding;
  // property_apply_now_url / property_website_url remain available to
  // editors via suggestedFields.
  const ctaLinkDef: SlotGroupDefinition = {
    label: 'CTA Link',
    fields: {
      url: {
        label: 'URL',
        type: 'url',
        defaultValue: ctaUrl,
        suggestedFields: ['property_apply_now_url', 'property_website_url'],
      },
      label: {
        label: 'Link Text',
        type: 'text',
        defaultValue: ctaLabel,
      },
      _target: {
        label: 'Target',
        type: 'text',
        defaultValue: '_self',
      },
    },
  };

  const cta = useSlotGroup(ctaPath, ctaLinkDef);
  const bgImage = useSlotGroup(bgPath, featuredImageDef);

  // Parallax refs — the background image shifts at 30 % of scroll speed to
  // create depth, matching the homepage Hero.
  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    // Respect reduced-motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      const scrolled = -rect.top;
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // seed the position on mount (handles pre-scrolled pages)
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // "Read more" behaviour for the blurb — on mobile only, a long blurb is
  // collapsed to ~half its natural height (with a soft fade) behind a toggle
  // so it never fills the banner. From the `sm` breakpoint up the full blurb
  // always shows and no toggle renders.
  const blurbRef = useRef<HTMLDivElement>(null);
  const [blurbExpanded, setBlurbExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [blurbFullHeight, setBlurbFullHeight] = useState<number | null>(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const el = blurbRef.current;
    if (!el) return;
    // scrollHeight reports the full content height regardless of the collapsed
    // maxHeight/overflow, so this stays accurate whether or not it's clamped.
    const measure = () => setBlurbFullHeight(el.scrollHeight);
    measure();
    window.addEventListener('resize', measure);
    // Re-measure once webfonts settle — the mono blurb face changes line height.
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }
    return () => window.removeEventListener('resize', measure);
  }, [isMobile, blurbField, defaultText]);

  // Only offer the toggle when the mobile blurb is tall enough to be worth
  // truncating (~8+ lines); short blurbs render in full with no button.
  const showBlurbToggle =
    isMobile && blurbFullHeight != null && blurbFullHeight > 160;
  const blurbCollapsed = showBlurbToggle && !blurbExpanded;
  const collapsedHeight =
    blurbFullHeight != null ? Math.round(blurbFullHeight * 0.5) : 0;

  return (
    <div className="relative w-full z-10 bg-[var(--vibe-color-background)]">
      <section
        ref={sectionRef}
        className="relative w-full text-white"
        style={{
          // Downward-pointing chevron: the bottom edge slopes from each side
          // down to a single point at the horizontal centre, revealing the
          // white page background as two triangular wedges. clip-path also
          // clips the oversized parallax image, replacing overflow-hidden.
          //
          // The section no longer sets its own height — the centred content
          // block below owns it via a min-height floor (max(98vh,720px)) and
          // grows to fit long titles/blurbs. The chevron percentages therefore
          // track the actual rendered height. Centered content clears it: the
          // deepest cut under the (horizontally centred) copy is the tip at
          // 90%, and the 22vh bottom padding keeps the last line clear of it.
          clipPath: 'polygon(0 0, 100% 0, 100% 76%, 50% 90%, 0 76%)',
        }}
      >
        {/* Parallax + Ken Burns animated background */}
        <EditableSlotGroup groupPath={bgPath} definition={featuredImageDef}>
          {/* Oversized wrapper: 140 % tall starting 20 % above the section top,
              and ~2 % wider than the section (1 % bleed on each side). The
              extra height gives the parallax translate room to move, and the
              extra width absorbs the Ken Burns horizontal translate. */}
          <div
            ref={imgWrapRef}
            className="absolute h-[140%]"
            style={{ top: '-20%', left: '-1%', right: '-1%', willChange: 'transform' }}
          >
            {bgImage.image ? (
              <VibesitesImage
                url={bgImage.image}
                alt={bgImage.alt || ''}
                priority
                sizes="100vw"
                className="w-full h-full object-cover"
                style={{
                  animation: 'heroSolidKenBurns 30s ease-in-out infinite alternate',
                  willChange: 'transform',
                  transformOrigin: 'bottom right',
                  imageRendering: 'high-quality' as any,
                }}
              />
            ) : (
              <div className="w-full h-full bg-[var(--vibe-color-primary)]" />
            )}
          </div>
        </EditableSlotGroup>

        {/* Primary-color overlay at 77% opacity, layered above the parallax
            image but below the content (z-10) so text and CTA stay legible. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 z-0 pointer-events-none"
          style={{ background: 'var(--vibe-color-primary)', opacity: 0.77 }}
        />

        {/* Content — vertically centred over the image, matching the Hero.
            This element (not the <section>) owns the height: a min-height floor
            keeps the banner tall on short pages, but because it's min-height
            (not a fixed height) the banner GROWS to fit a long title/blurb
            instead of overflowing. That overflow was what pushed the title up
            under the header nav and the blurb down through the chevron on
            mobile. The top padding reserves room for the fixed header (and
            offer banner when present) so the title never tucks under the
            navigation menu; the bottom padding keeps the last line of copy
            clear of the chevron clip-path (which cuts up to ~24% off the
            bottom edge) so the blurb never gets cut off — both are honoured at
            any content length now that the section can expand. */}
        <div
          className="relative z-10 flex flex-col items-center justify-center min-h-[max(98vh,720px)] text-center px-6 sm:px-8 pointer-events-none"
          style={{
            paddingTop: 'calc(var(--offer-banner-height, 0px) + 6rem)',
            paddingBottom: '22vh',
          }}
        >
          {/* Page Title */}
          <div className="min-w-0 w-full pointer-events-auto">
            <RichTextContent
              path={titlePath}
              defaultValue={`<p>${title}</p>`}
              as="h1"
              className="hero-solid-title-font text-xl sm:text-4xl md:text-5xl lg:text-6xl font-normal uppercase tracking-normal sm:tracking-wider leading-tight break-words hyphens-auto text-center drop-shadow-lg"
            />
          </div>

          {/* Blurb — collapses to ~half height behind a Read More toggle on
              mobile (see the blurb hooks above); always full from `sm` up. */}
          <div className="mt-6 w-full max-w-3xl pointer-events-auto">
            <div
              ref={blurbRef}
              style={
                blurbCollapsed
                  ? {
                      maxHeight: `${collapsedHeight}px`,
                      overflow: 'hidden',
                      WebkitMaskImage:
                        'linear-gradient(to bottom, #000 55%, transparent 100%)',
                      maskImage:
                        'linear-gradient(to bottom, #000 55%, transparent 100%)',
                    }
                  : undefined
              }
            >
              <RichTextContent
                path={blurbPath}
                defaultField={blurbField}
                defaultValue={defaultText || ''}
                as="div"
                className="hero-solid-blurb-font text-base font-normal not-italic leading-relaxed opacity-90 [&_a]:underline text-center drop-shadow-md"
              />
            </div>
            {showBlurbToggle && (
              <button
                type="button"
                onClick={() => setBlurbExpanded((v) => !v)}
                aria-expanded={blurbExpanded}
                className="hero-solid-readmore font-heading text-xs uppercase tracking-wider underline underline-offset-4 mt-3 sm:hidden"
              >
                {blurbExpanded ? 'Read Less' : 'Read More'}
              </button>
            )}
          </div>

          {/* CTA */}
          {showCta && (
            <div className="text-center mt-16 md:mt-20 pointer-events-auto">
              <EditableSlotGroup groupPath={ctaPath} definition={ctaLinkDef}>
                <a
                  href={cta.url || '#'}
                  target={cta._target || '_self'}
                  className="hero-solid-cta-btn group/cta relative inline-flex items-center gap-2 px-10 py-4 font-heading text-sm uppercase tracking-wider overflow-hidden transition-all duration-300"
                >
                  <span className="relative z-10">{cta.label || ctaLabel}</span>
                </a>
              </EditableSlotGroup>
            </div>
          )}
        </div>

        <style>{`
          /* Match FloorplansList's feature_title: Oswald at normal weight,
             applied to the element and all of its rich-text children. */
          .hero-solid-title-font,
          .hero-solid-title-font * {
            font-family: 'Oswald', 'Arial Narrow', system-ui, sans-serif;
            font-weight: 400;
          }
          /* Match FloorplansList's feature_body (.fp-feature-body-font):
             Space Mono at normal weight/style, applied to the element and
             all of its rich-text children. */
          .hero-solid-blurb-font,
          .hero-solid-blurb-font * {
            font-family: 'Space Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
            font-weight: 400;
            font-style: normal;
            /* Pin the blurb (and every rich-text child) to a single size so it
               renders identically on every inner-page header. On mobile the
               monospace copy is dropped to 0.8125rem (13px) with tighter line
               spacing so a multi-line property blurb fits inside the banner
               instead of overflowing into the header / chevron; it steps back
               up to 16px at the sm breakpoint. */
            font-size: 0.8125rem;
            line-height: 1.5;
          }
          @media (min-width: 640px) {
            .hero-solid-blurb-font,
            .hero-solid-blurb-font * {
              font-size: 1rem;
              line-height: 1.625;
            }
          }
          /* Read More toggle — subtle white link over the primary overlay,
             brightening on hover; matches the heading (Oswald) family. */
          .hero-solid-readmore {
            color: #fff;
            opacity: 0.85;
            cursor: pointer;
            transition: opacity 0.2s ease;
          }
          .hero-solid-readmore:hover {
            opacity: 1;
          }
          @keyframes heroSolidKenBurns {
            0% { transform: scale(1.01) translate(0, 0); }
            100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
          }
          /* Mirrors the homepage Hero's .hero-cta: a solid secondary fill that
             inverts to white on hover, sitting over the image + overlay. */
          .hero-solid-cta-btn {
            background: var(--vibe-color-secondary);
            border: 1.5px solid var(--vibe-color-secondary);
            border-radius: 2px;
            color: #fff;
            box-shadow: none;
          }
          .hero-solid-cta-btn:hover {
            background: #fff;
            border-color: #fff;
            color: var(--vibe-color-secondary);
            transform: translateY(-2px);
            box-shadow: none;
          }
          .hero-solid-cta-btn:active {
            transform: translateY(0);
            box-shadow: none;
          }
        `}</style>
      </section>

      {/* Vertical accent line centred on the chevron tip — the clip-path dips
          to its lowest point (90% of the hero height) at the horizontal centre,
          which is exactly where the image meets the white space along this
          column. Lives in the wrapper (not the clipped section) so the chevron
          clip-path doesn't cut it off. */}
      <div
        aria-hidden="true"
        className="absolute left-1/2 -translate-x-1/2 w-[3px] h-[16%] z-20"
        style={{ background: 'var(--vibe-color-secondary)', bottom: '2%' }}
      />

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="herosolidbg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.blurb"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="herosolidbg.bg_image"
        definition={featuredImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="herosolidbg.cta_link"
        definition={ctaLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </div>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroSolidBG',
  displayName: 'Hero – Solid Background',
  tokens: {
    'herosolidbg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
    'herosolidbg.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'herosolidbg.bg_image': featuredImageDef,
    'herosolidbg.cta_link': {
      label: 'CTA Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/floor-plans',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View Floor Plans',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
  },
};

(HeroSolidBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroSolidBG;
